import React, { useRef, useEffect, ReactNode } from "react";

interface DraggableCarouselProps {
  children: ReactNode;
  className?: string;
  gap?: string;
  activeIndex?: number;
  itemClassName?: string;
}

export function DraggableCarousel({
  children,
  className = "w-full overflow-hidden",
  gap = "gap-2",
  activeIndex,
  itemClassName = "",
}: DraggableCarouselProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);

  const isDragging = useRef(false);
  const hasMoved = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollerRef.current) return;

    isDragging.current = true;
    hasMoved.current = false;

    startX.current = e.pageX - scrollerRef.current.offsetLeft;
    scrollLeft.current = scrollerRef.current.scrollLeft;

    scrollerRef.current.style.cursor = "grabbing";
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !scrollerRef.current) return;

    const x = e.pageX - scrollerRef.current.offsetLeft;
    const walk = x - startX.current;

    if (Math.abs(walk) > 5) {
      hasMoved.current = true;
    }

    if (hasMoved.current) {
      scrollerRef.current.scrollLeft = scrollLeft.current - walk * 1.5;
    }
  };

  const stopDrag = () => {
    if (!scrollerRef.current) return;

    isDragging.current = false;
    hasMoved.current = false;

    scrollerRef.current.style.cursor = "grab";
    scrollLeft.current = scrollerRef.current.scrollLeft;
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!scrollerRef.current) return;

    isDragging.current = true;
    hasMoved.current = false;

    startX.current = e.touches[0].pageX - scrollerRef.current.offsetLeft;
    scrollLeft.current = scrollerRef.current.scrollLeft;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current || !scrollerRef.current) return;

    const x = e.touches[0].pageX - scrollerRef.current.offsetLeft;
    const walk = x - startX.current;

    if (Math.abs(walk) > 5) {
      hasMoved.current = true;
    }

    if (hasMoved.current) {
      scrollerRef.current.scrollLeft = scrollLeft.current - walk * 1.5;
    }
  };

  useEffect(() => {
    if (activeIndex === undefined || !scrollerRef.current) return;

    const scroller = scrollerRef.current;
    const items = scroller.children;

    const activeEl = items[activeIndex] as HTMLElement;
    if (!activeEl) return;

    const scrollerWidth = scroller.clientWidth;
    const elCenter = activeEl.offsetLeft + activeEl.offsetWidth / 2;
    const targetScroll = elCenter - scrollerWidth / 2;
    const maxScroll = scroller.scrollWidth - scrollerWidth;

    scroller.scrollTo({
      left: Math.max(0, Math.min(targetScroll, maxScroll)),
      behavior: "smooth",
    });
  }, [activeIndex]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.style.cursor = "grab";
    }
  }, []);

  return (
    <div className={className}>
      <div
        ref={scrollerRef}
        className={`flex ${gap} flex-nowrap overflow-x-auto overscroll-x-contain select-none scrollbar-hide touch-pan-y ${itemClassName}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={stopDrag}
        onMouseLeave={stopDrag}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={stopDrag}
      >
        {children}
      </div>
    </div>
  );
}
